import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { Badge, Card, EmptyState, PageHeader, Table, Td, Th, Tr } from '../components/ui';
import { Loaded, Note, PersonName, useCompany, useDates } from '../components/b-shared';

/**
 * Dashboard Access (CR-008 §3.1), read-only: who may open this dashboard. Access is granted and revoked in the
 * admin (Settings › Executive Dashboard); a grant still recorded for someone who is no longer an active
 * Administrator is not honoured, and their session ends on its next request.
 */

interface Holder {
  user: { id: string; fullName: string; fullNameAr: string | null; jobTitle: string | null };
  grantedAt: string;
  grantedBy: { id: string; fullName: string; fullNameAr: string | null } | null;
  lastSignInAt: string | null;
  honoured: boolean;
}

export function AccessPage() {
  const { t } = useI18n();
  const dates = useDates();
  const { timezone } = useCompany();
  const holders = useQuery({ queryKey: ['executive', 'access'], queryFn: () => api.get<Holder[]>('/executive/access') });
  return (
    <>
      <PageHeader title={t('access.title')} subtitle={t('access.subtitle')} count={holders.data?.length} />
      <div className="mb-5 space-y-1.5">
        <Note>{t('access.readOnly')}</Note>
        <Note>{t('access.who')}</Note>
      </div>
      <Loaded query={holders}>
        {(rows) => (
          <Card padded={false}>
            {rows.length === 0 ? (
              <EmptyState title={t('access.empty')} hint={t('access.emptyHint')} />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>{t('access.col.name')}</Th>
                    <Th>{t('access.col.jobTitle')}</Th>
                    <Th>{t('access.col.grantedAt')}</Th>
                    <Th>{t('access.col.grantedBy')}</Th>
                    <Th>{t('access.col.lastSignIn')}</Th>
                    <Th>{t('access.col.state')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((h) => (
                    <Tr key={h.user.id}>
                      <Td className="min-w-[11rem] font-medium">
                        <PersonName person={h.user} />
                      </Td>
                      <Td className="text-[var(--text-secondary)]">{h.user.jobTitle || '—'}</Td>
                      <Td className="whitespace-nowrap">{dates.at(h.grantedAt, timezone)}</Td>
                      <Td>{h.grantedBy ? <PersonName person={h.grantedBy} /> : <span className="text-muted">{t('access.grantedBySystem')}</span>}</Td>
                      <Td className="whitespace-nowrap">{h.lastSignInAt ? dates.at(h.lastSignInAt, timezone) : <span className="text-muted">{t('access.never')}</span>}</Td>
                      <Td className="min-w-[12rem]">
                        {h.honoured ? (
                          <Badge tone="success">{t('access.honoured')}</Badge>
                        ) : (
                          <span className="flex flex-col items-start gap-1">
                            <Badge tone="warning">{t('access.notHonoured')}</Badge>
                            <span className="text-caption text-muted">{t('access.notHonouredHint')}</span>
                          </span>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        )}
      </Loaded>
    </>
  );
}
